'use client'

import { useSlotProps } from '@mui/base'
import { clsx } from 'clsx'
import { type ComponentPropsWithRef, type JSX, type ReactElement } from 'react'

export type IconListItemProps = ComponentPropsWithRef<'li'> & {
  icon: ReactElement | null
  slotProps?: {
    content?: ComponentPropsWithRef<'div'>
    iconContainer?: ComponentPropsWithRef<'div'>
  }
}

/**
 * The `IconListItem` is used to represent an item in a list. It must be contained in a parent unordered list (`<ul>`) element.
 * In unordered lists, they are displayed with an ascending system icon (`@redteclab/icons`) on the left.
 */
export const IconListItem = (props: IconListItemProps): JSX.Element => {
  const { children, className, icon, slotProps = {}, ...rest } = props

  const contentProps = useSlotProps({
    className: 'icon-list-item__content',
    elementType: 'div',
    externalSlotProps: slotProps.content,
    ownerState: undefined,
  })

  const iconContainerProps = useSlotProps({
    className: 'icon-list-item__icon-container',
    elementType: 'div',
    externalSlotProps: slotProps.iconContainer,
    ownerState: undefined,
  })

  return (
    <li className={clsx('icon-list-item', className)} {...rest}>
      {icon ? <div {...iconContainerProps}>{icon}</div> : null}
      <div {...contentProps}>{children}</div>
    </li>
  )
}
